import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "../adminApi";
import { Panel, buttonClass, inputClass } from "../components/Bits";
import { CampaignChip } from "./AdminPipeline";

const TIMING = ["Day offset", "Recurring", "Relative to renewal", "On signal"];
const AUTOMATION = ["Auto", "Template", "Task"];

const timingValue = (step) => {
  if (step.timing_type === "Day offset") return `Day ${step.day_offset}`;
  if (step.timing_type === "Recurring") return step.recurrence;
  if (step.timing_type === "Relative to renewal") return `Renewal ${step.renewal_offset_days} days`;
  return "On signal only";
};

const StepRow = ({ step, onSave }) => {
  const [draft, setDraft] = useState(null);

  if (!draft) {
    return (
      <li className="flex flex-wrap items-start justify-between gap-3 py-3"
          data-testid={`step-${step.step_id}`}>
        <div className="min-w-0">
          <p className="text-sm text-cream/85">
            {step.step_id} · {step.channel} · {step.automation}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-cream/50">{step.content}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
            {timingValue(step)} · CTA: {step.cta}
          </p>
        </div>
        <button type="button" data-testid={`step-edit-${step.step_id}`}
                onClick={() => setDraft({ ...step })}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-gold">
          Edit
        </button>
      </li>
    );
  }

  const field = (key, props = {}) => (
    <input className={inputClass} value={draft[key] ?? ""} data-testid={`step-${key}-${step.step_id}`}
           onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} {...props} />
  );

  return (
    <li className="space-y-2 py-4" data-testid={`step-editor-${step.step_id}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{step.step_id}</p>
      <div className="grid gap-2 sm:grid-cols-3">
        <select className={inputClass} value={draft.timing_type}
                data-testid={`step-timing-${step.step_id}`}
                onChange={(e) => setDraft({ ...draft, timing_type: e.target.value })}>
          {TIMING.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
        {draft.timing_type === "Day offset" ? field("day_offset", { type: "number" }) : null}
        {draft.timing_type === "Recurring" ? field("recurrence") : null}
        {draft.timing_type === "Relative to renewal"
          ? field("renewal_offset_days", { type: "number" }) : null}
        <select className={inputClass} value={draft.automation}
                data-testid={`step-automation-${step.step_id}`}
                onChange={(e) => setDraft({ ...draft, automation: e.target.value })}>
          {AUTOMATION.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </div>
      {field("content", { placeholder: "What this touch is" })}
      {field("cta", { placeholder: "Call to action" })}
      {field("email_subject", { placeholder: "Email subject" })}
      <textarea className={inputClass} rows={5} value={draft.email_body ?? ""}
                data-testid={`step-body-${step.step_id}`}
                onChange={(e) => setDraft({ ...draft, email_body: e.target.value })} />
      <div className="flex items-center gap-3">
        <button type="button" className={buttonClass} data-testid={`step-save-${step.step_id}`}
                onClick={async () => { await onSave(step.step_id, draft); setDraft(null); }}>
          Save step
        </button>
        <button type="button" onClick={() => setDraft(null)}
                className="text-xs text-cream/40 transition-colors duration-200 hover:text-cream">
          Cancel
        </button>
      </div>
    </li>
  );
};

const ComplianceCard = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    adminApi.get("/crm/settings/compliance").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  if (!settings) return null;

  const save = async (event) => {
    event.preventDefault();
    try {
      const { data } = await adminApi.put("/crm/settings/compliance", {
        business_address: settings.business_address,
        unsubscribe_url: settings.unsubscribe_url,
      });
      setSettings(data);
      toast.success("Compliance details saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel title="Compliance" testid="panel-compliance">
      <p className="text-sm leading-relaxed text-cream/55">{settings.note}</p>
      <form onSubmit={save} className="mt-4 space-y-3" data-testid="compliance-form">
        <input className={inputClass} data-testid="compliance-address"
               placeholder="Business mailing address (required on every automated email)"
               value={settings.business_address}
               onChange={(e) => setSettings({ ...settings, business_address: e.target.value })} />
        <input className={inputClass} data-testid="compliance-unsubscribe"
               placeholder="Unsubscribe link, e.g. https://ohcbenefits.com/unsubscribe"
               value={settings.unsubscribe_url}
               onChange={(e) => setSettings({ ...settings, unsubscribe_url: e.target.value })} />
        <button type="submit" className={buttonClass} data-testid="compliance-save">Save</button>
      </form>
      <p className="mt-4 text-xs leading-relaxed text-cream/40" data-testid="sending-status">
        {settings.sending_enabled
          ? "Automatic sending is connected — Auto steps go out on their due date from your own domain."
          : "Automatic sending is not connected yet, so every Auto step lands as an \u201cEmail ready to send\u201d task with the draft inside. Connecting your own sending domain turns on true auto-send."}
      </p>
    </Panel>
  );
};

const UnsubscribesCard = () => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    adminApi.get("/crm/suppressions")
      .then(({ data }) => setRows(data.suppressions)).catch(() => setRows([]));
  }, []);

  if (!rows) return null;

  return (
    <Panel title="Unsubscribes" testid="panel-unsubscribes"
           action={<span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
             {rows.length} suppressed
           </span>}>
      <p className="text-sm leading-relaxed text-cream/55">
        Read-only. Every address here is skipped by every campaign and sequence.
      </p>
      <ul className="mt-4 divide-y divide-cream/10" data-testid="unsubscribe-list">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5"
              data-testid={`unsubscribe-row-${row.id}`}>
            <span className="text-sm text-cream/85">{row.email}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
              {(row.created_at || "").slice(0, 10)} · {row.source}
            </span>
          </li>
        ))}
        {rows.length === 0 ? (
          <li className="py-2.5 text-sm text-cream/40" data-testid="unsubscribe-empty">
            Nobody has unsubscribed yet.
          </li>
        ) : null}
      </ul>
    </Panel>
  );
};

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);

  const load = useCallback(() => {
    adminApi.get("/crm/nurture/campaigns")
      .then(({ data }) => setCampaigns(data.campaigns)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveStep = async (stepId, draft) => {
    try {
      await adminApi.patch(`/crm/nurture/steps/${stepId}`, {
        timing_type: draft.timing_type,
        day_offset: draft.day_offset === "" ? null : Number(draft.day_offset),
        recurrence: draft.recurrence || null,
        renewal_offset_days: draft.renewal_offset_days === "" ? null
          : Number(draft.renewal_offset_days),
        automation: draft.automation,
        content: draft.content,
        cta: draft.cta,
        email_subject: draft.email_subject,
        email_body: draft.email_body,
      });
      toast.success(`${stepId} saved`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-campaigns-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Campaigns</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          The nurture library
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          Plant opens the relationship, Grow works the evaluation, Replant keeps a &ldquo;not
          now&rdquo; warm until the renewal comes back around. Edit any step and every deal put on
          that campaign from then on uses the new wording and timing.
        </p>
      </div>

      <ComplianceCard />
      <UnsubscribesCard />

      {campaigns.filter((campaign) => campaign.campaign_id !== "none").map((campaign) => (
        <Panel key={campaign.campaign_id} title={campaign.name}
               testid={`panel-campaign-${campaign.campaign_id}`}
               action={<div className="flex items-center gap-3">
                 <CampaignChip id={campaign.campaign_id} />
                 <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                   {campaign.deals} deals
                 </span>
               </div>}>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[["Goal", campaign.goal], ["Entry", campaign.entry_criteria],
              ["Exit", campaign.exit_trigger], ["Length", campaign.length],
              ["Frequency", campaign.frequency], ["Primary CTA", campaign.primary_cta]]
              .map(([label, value]) => (
                <div key={label}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                    {label}
                  </dt>
                  <dd className="mt-1 text-xs leading-relaxed text-cream/70">{value || "—"}</dd>
                </div>
              ))}
          </dl>
          <ul className="mt-5 divide-y divide-cream/10">
            {campaign.steps.map((step) => (
              <StepRow key={step.step_id} step={step} onSave={saveStep} />
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}
